<template>
  <FormQuestion
    @back="emit('back')"
    @click:notes="emit('click:notes')"
    @next="emit('next')"
    :bold="question.is_bold"
    :class="landscape ? 'my-0' : 'mb-6'"
    :color="color"
    :dense="dense"
    :display-notes="displayNotes"
    :elevation="elevation"
    :header="header"
    :hide-actions="hideActions"
    :landscape="landscape"
    :mandatory="question.mandatory"
    :model-value="modelValue"
    :next-disabled="nextDisabled"
    :notes-count="notesCount"
    :outlined="outlined"
    :paddingless="paddingless"
    :processing="processing"
    :published="question.published"
    :reversible="reversible"
    :show-indicators="showIndicators"
    :subtitle="question.subtitle"
    :tile="tile"
    :title="question.title"
    :very-dense="veryDense"
    data-testid="provider-question"
  >
    <div
      v-if="showSelectedProvider"
      class="fs-14"
      data-testid="provider-question-readonly"
    >
      <span v-if="loadingSelectedProvider">{{ $bct('Loading...') }}</span>
      <span v-else-if="selectedProvider">{{ selectedProvider.name }}</span>
      <span v-else-if="providerId">{{ providerId }}</span>
      <span v-else>{{ $bct('No provider selected.') }}</span>
    </div>

    <div
      v-else
      class="d-flex flex-column ga-3"
    >
      <p data-testid="provider-question-search-hint">
        {{ $bct('Search for a provider by name.') }}
      </p>

      <StandardSearch
        v-model="selectedProviderId"
        :aria-label="$bct('Search by provider name')"
        :hide-no-data="true"
        :items="searchedProviders"
        :loading="searching || loadingSelectedProvider"
        :no-filter="true"
        :on-search-input="searchProviders"
        :placeholder="$bct('Search')"
        data-testid="provider-question-autocomplete"
        input-class="w-100"
        item-title="name"
        item-value="id"
        variant="autocomplete"
      />

      <v-alert
        v-if="showProviderChangeNotice"
        :text="
          $bct(
            'Changing the provider may impact automated team assignments and workflows associated with this form.',
          )
        "
        data-testid="provider-change-notice"
        title="Notice"
        type="warning"
        variant="tonal"
      />
    </div>
  </FormQuestion>
</template>

<script setup>
import FormQuestion from '@/shared/components/form/FormQuestion.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const { api: Api, scopedApi } = useApi();
const eventBus = useEventBus();

const $bct = inject('translator');

const modelValue = defineModel({ type: Object });

const props = defineProps({
  color: {
    default: 'transparent',
    type: String,
  },
  dense: {
    default: false,
    type: Boolean,
  },
  displayNotes: {
    type: Boolean,
    default: false,
  },
  veryDense: {
    type: Boolean,
    default: false,
  },
  elevation: {
    default: 0,
    type: Number,
  },
  expanded: {
    default: false,
    type: Boolean,
  },
  header: {
    type: String,
    default: null,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  landscape: {
    type: Boolean,
    default: false,
  },
  notesCount: {
    type: Number,
    default: null,
  },
  outlined: {
    type: Boolean,
    default: false,
  },
  paddingless: {
    type: Boolean,
    default: false,
  },
  processing: {
    default: false,
    type: Boolean,
  },
  question: {
    default: () => ({}),
    type: Object,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  reversible: {
    type: Boolean,
    default: true,
  },
  showIndicators: {
    type: Boolean,
    default: false,
  },
  showProviderChangeNotice: {
    type: Boolean,
    default: false,
  },
  tile: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['back', 'change', 'change:input', 'click:notes', 'next']);

const searchedProviders = ref([]);
const searching = ref(false);
const loadingSelectedProvider = ref(false);
const selectedProvider = ref(null);

const mandatoryAttributes = computed(() => {
  if (props.question.mandatory) {
    return props.question.synced_attributes?.length
      ? props.question.synced_attributes
      : [providerAttribute.value];
  }

  return props.question.mandatory_attributes || [];
});

const nextDisabled = computed(() => {
  return props.question.mandatory && !providerId.value;
});

const providerAttribute = computed(() => {
  return props.question.synced_attributes?.[0] ?? { name: providerAttributeName.value };
});

const providerAttributeName = computed(() => {
  return props.question.synced_attributes?.[0]?.name || 'provider_id';
});

const providerApi = computed(() => scopedApi?.provider ?? Api.publicApi.organization.provider);

const providerId = computed(() => {
  const attributeName = providerAttributeName.value;
  if (attributeName.startsWith('custom.')) {
    return modelValue.value?.custom?.[attributeName.split('.')[1]];
  }

  return modelValue.value?.[attributeName];
});

const selectedProviderId = computed({
  get() {
    return providerId.value;
  },
  set(value) {
    const normalizedValue = value || null;
    setProviderId(normalizedValue);
    emitProviderChange(normalizedValue);
  },
});

const showSelectedProvider = computed(() => {
  return props.readonly;
});

watch(providerId, loadSelectedProvider, { immediate: true });

function emitProviderChange(value) {
  const attribute = providerAttribute.value;
  let empty = false;

  if (isMandatoryAttribute(attribute)) {
    empty = value == null || value.toString().length === 0;
  }

  emit('change', {
    attribute,
    value,
    empty,
  });
  emit('change:input', {
    attribute,
    value,
    empty,
  });
}

async function fetchProvider(providerIdValue) {
  return providerApi.value.get(providerIdValue);
}

function isMandatoryAttribute(attribute) {
  return mandatoryAttributes.value.some(
    (mandatoryAttribute) => mandatoryAttribute.name === attribute.name,
  );
}

async function loadSelectedProvider() {
  const currentProviderId = providerId.value;

  if (!currentProviderId) {
    selectedProvider.value = null;
    if (!showSelectedProvider.value) {
      searchedProviders.value = [];
    }
    return;
  }

  const cachedProvider = searchedProviders.value.find(
    (provider) => provider.id === currentProviderId,
  );
  if (cachedProvider) {
    selectedProvider.value = cachedProvider;
    return;
  }

  loadingSelectedProvider.value = true;
  try {
    const response = await fetchProvider(currentProviderId);
    const provider = response?.data;
    if (provider) {
      selectedProvider.value = provider;
      if (!showSelectedProvider.value) {
        searchedProviders.value = [provider];
      }
    }
  } catch {
    selectedProvider.value = null;
  } finally {
    loadingSelectedProvider.value = false;
  }
}

async function searchProviders(query) {
  if (showSelectedProvider.value) return;

  if (!query) {
    if (providerId.value && selectedProvider.value) {
      searchedProviders.value = [selectedProvider.value];
    } else {
      searchedProviders.value = [];
    }
    return;
  }

  searching.value = true;
  try {
    const response = await providerApi.value.index({ query });
    if (!response?.data) {
      throw new Error('Provider search failed');
    }
    searchedProviders.value = response.data;
  } catch {
    searchedProviders.value =
      providerId.value && selectedProvider.value ? [selectedProvider.value] : [];
    eventBus.error('Unable to search providers. Please try again.');
  } finally {
    searching.value = false;
  }
}

function setProviderId(value) {
  const attributeName = providerAttributeName.value;
  if (attributeName.startsWith('custom.')) {
    if (!modelValue.value.custom) {
      modelValue.value.custom = {};
    }
    modelValue.value.custom[attributeName.split('.')[1]] = value;
  } else {
    modelValue.value[attributeName] = value;
  }
}
</script>
